import { Card, CardContent } from '../../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import {
  HelpCircle, Scale, Shield, FileText, Mail, Play, Clock, Timer,
  AlertTriangle, PoundSterling, PhoneCall, LifeBuoy, ChevronRight,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useIsLargeScreen } from '../../hooks/useIsLargeScreen';

// Feb 2026 Pilot Readiness Sprint — the pilot pricing is gone. Support
// SLA copy is written for the live commercial model.
const SUPPORT_SLA = 'Support responses usually within 1–2 business days.';

// Collapsible help sections — first section is expanded by default so users
// always see something when they open the page (nothing worse than a page of
// closed accordions and no visible content).
const HELP_SECTIONS = [
  {
    key: 'how-it-works',
    icon: Play,
    color: 'bg-blue-100 text-blue-700',
    title: 'How ShiftCover365 works',
    content: [
      'ShiftCover365 connects retail branch managers with verified shift workers.',
      'Managers post shifts. Workers apply. Once accepted, you turn up, clock in, and get paid via the platform.',
      'All till checks, timings and accountability records are captured in-app so managers have a clean audit trail.',
    ],
  },
  {
    key: 'explain-my-shifts',
    icon: FileText,
    color: 'bg-slate-100 text-slate-700',
    title: 'Explain my shifts',
    content: [
      'Available shifts: shifts you haven\'t applied to yet — tap Apply to be considered.',
      'Pending: your application is with the manager. They can accept or decline.',
      'Accepted: you\'ve been picked. Turn up on the day, on time.',
      'In progress: you\'ve clocked in and the shift is currently running.',
      'Completed: the shift finished cleanly with till checks submitted.',
      'Cancelled: the shift was called off (by you or the manager). Repeated late cancellations reduce your reliability score.',
    ],
  },
  {
    key: 'when-to-start',
    icon: Clock,
    color: 'bg-emerald-100 text-emerald-700',
    title: 'When to start a shift',
    content: [
      'Open the app and go to My Shifts on the day of your shift.',
      'Tap Start shift no earlier than 15 minutes before the scheduled start time and no later than the scheduled start time.',
      'Complete the initial till check right after clocking in so the opening till is on record.',
      'Once started, you\'re counted as on-shift and the manager can see you\'re active.',
    ],
  },
  {
    key: 'when-to-end',
    icon: Timer,
    color: 'bg-emerald-100 text-emerald-700',
    title: 'When to end a shift',
    content: [
      'Complete the final till count in the app before ending the shift.',
      'Tap End shift once the till log is submitted and you\'re ready to leave.',
      'Ending early without a good reason is recorded as "Left early" and reduces your reliability score.',
      'If a customer walks in at the last minute and you finish 5 minutes late, no penalty — just clock out when you\'re done.',
    ],
  },
  {
    key: 'if-late',
    icon: AlertTriangle,
    color: 'bg-amber-100 text-amber-700',
    title: 'What happens if I\'m late',
    content: [
      'A "Started late" flag is added to the shift when you clock in after the scheduled start time.',
      'The flag is visible to the manager and reduces your reliability score.',
      'Being late does NOT mean you get automatically marked as leaving early — the two flags are independent.',
      'If you know you\'re going to be late, message the manager from the shift page as soon as possible.',
    ],
  },
  {
    key: 'forgot-to-clock',
    icon: HelpCircle,
    color: 'bg-rose-100 text-rose-700',
    title: 'What happens if I forget to clock in',
    content: [
      'Message the manager from the shift page as soon as you realise. They can add a manual time entry.',
      'The system won\'t auto-close the shift — you can still clock in and complete till checks even if you\'re late.',
      'Consistently forgetting to clock in reduces your reliability score, so set a reminder on your phone if you need to.',
    ],
  },
  {
    key: 'payment',
    icon: PoundSterling,
    color: 'bg-emerald-100 text-emerald-700',
    title: 'Payment FAQs',
    content: [
      'Managers pay ShiftCover365 for their location(s) via a chosen plan: Pay-As-You-Go (£3.99 per successfully completed shift, billed monthly), Monthly (£29.99/location), Annual (£290/location) or Multi-site (£99/month, up to 5 locations). Every new manager gets a 14-day free trial with no card required.',
      'Workers are paid directly by the branch — payment terms are agreed with the manager. ShiftCover365 does not currently process worker wages.',
      'Your pay rate for each shift is set by the manager and shown on the shift card before you apply.',
      'If a payment issue comes up, message the manager first. If that doesn\'t resolve it, email support.',
    ],
  },
  {
    key: 'who-to-contact',
    icon: PhoneCall,
    color: 'bg-blue-100 text-blue-700',
    title: 'Who to contact',
    content: [
      'For shift-specific issues (timing, pay, task questions): message the manager on the shift page.',
      'For technical problems (login, uploads, notifications not arriving): email support@shiftcover365.co.uk.',
      'For account deletion, GDPR requests or a copy of your data: use Profile → Legal & Privacy.',
      SUPPORT_SLA,
    ],
  },
];

const LEGAL_SECTIONS = [
  {
    key: 'smart-id',
    icon: Shield,
    color: 'bg-emerald-100 text-emerald-700',
    title: 'Smart ID Verification',
    content: [
      'Workers must self-declare their Smart ID status during onboarding.',
      'Managers then confirm the Smart ID through an HR verification process.',
      'Only Smart ID verified workers can apply for shifts.',
    ],
  },
  {
    key: 'cancel-penalty',
    icon: Scale,
    color: 'bg-amber-100 text-amber-700',
    title: 'Cancellation & Reliability',
    content: [
      'Workers may cancel assigned shifts before they start.',
      'Late cancellations (close to the shift start time) are recorded and reduce your reliability score.',
      'No-shows are recorded and significantly reduce your reliability score.',
      'Your reliability score is visible to all managers when reviewing applicants.',
      'Workers with persistently low reliability scores may be suspended from the platform.',
    ],
  },
  {
    key: 'till',
    icon: FileText,
    color: 'bg-purple-100 text-purple-700',
    title: 'Till Accountability',
    content: [
      'Workers must submit a 3-hour periodic till check during active shifts.',
      'A final till log is required at the end of every shift.',
      'Discrepancies are automatically flagged and reported to managers.',
      'All till records are stored for audit and accountability purposes.',
    ],
  },
  {
    key: 'billing',
    icon: Shield,
    color: 'bg-emerald-100 text-emerald-800',
    title: 'Billing & Free Trial',
    content: [
      'Every new manager account starts with a 14-day free trial — no card required.',
      'Add a payment method any time before day 14 to keep using a paid plan.',
      "If you don't add a card by day 14, we automatically move you to Pay-As-You-Go so you're only charged £3.99 per successfully completed shift, billed monthly.",
      'Paid plans: Monthly £29.99/location, Annual £290/location (~£24.17/month equivalent), Multi-site £99/month for up to 5 locations.',
      'Founding Member offer: the first 20 Monthly customers pay £19.99/month for as long as their subscription stays active.',
      'Cancel any time from Manager → Billing → Cancel subscription.',
    ],
  },
  {
    key: 'gdpr-delete',
    icon: Shield,
    color: 'bg-rose-100 text-rose-700',
    title: 'Deleting your account (GDPR)',
    content: [
      'Tap "Delete account" in Profile, or submit a request at /account-deletion when signed out.',
      'Your account is deactivated immediately — you are signed out and can no longer log in.',
      'A GDPR erasure request is created automatically. Our team reviews and actions it within one month, in line with UK GDPR Article 17.',
      'Approved erasure removes your profile, shifts, ratings, chat messages, till logs and most accountability records.',
      'Payment transactions are retained for 7 years (HMRC obligation). A small set of audit-log rows is kept indefinitely so we can prove compliance with UK GDPR.',
    ],
  },
];

function AccordionSection({ item, defaultOpen = false }) {
  const Icon = item.icon;
  return (
    <AccordionItem
      value={item.key}
      className="border border-slate-200 rounded-xl bg-white overflow-hidden mb-3 last:mb-0"
      data-testid={`help-section-${item.key}`}
    >
      <AccordionTrigger className="px-4 py-3.5 hover:no-underline hover:bg-slate-50/50 [&[data-state=open]]:bg-slate-50/50">
        <div className="flex items-center gap-3 flex-1 text-left">
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${item.color}`}>
            <Icon className="h-4 w-4" />
          </div>
          <span className="text-[13.5px] font-semibold text-slate-900">{item.title}</span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4 pt-0">
        <ul className="space-y-2 ml-1">
          {item.content.map((line, j) => (
            <li key={j} className="flex items-start gap-2 text-[13px] text-slate-600 leading-relaxed">
              <ChevronRight className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </AccordionContent>
    </AccordionItem>
  );
}

export default function HelpLegalPage() {
  const { user } = useAuth();
  const isLargeScreen = useIsLargeScreen();
  const walkthroughId = user?.role === 'worker' && isLargeScreen
    ? 'wt-worker-help'
    : user?.role === 'manager' && isLargeScreen
      ? 'wt-manager-help'
      : undefined;
  const firstKey = HELP_SECTIONS[0]?.key;

  return (
    <div className="p-6 space-y-6 max-w-3xl mx-auto" data-testid="help-legal-page">
      <div>
        <h1
          className="text-2xl md:text-3xl font-bold text-slate-900 font-['Public_Sans']"
          data-walkthrough={walkthroughId}
        >
          Help &amp; Legal
        </h1>
        <p className="text-slate-500 mt-1">Information, policies, and support.</p>
        <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 px-3.5 py-2.5 flex items-start gap-2" data-testid="help-support-sla-banner">
          <LifeBuoy className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" />
          <p className="text-[12px] text-emerald-900 leading-snug">
            <span className="font-semibold">Support:</span> {SUPPORT_SLA} We&apos;re a small team &mdash; thanks for your patience!
          </p>
        </div>
      </div>

      {/* Help sections — collapsible */}
      <div>
        <h2 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.12em] mb-2.5">Help &amp; how-to</h2>
        <Accordion type="single" collapsible defaultValue={firstKey} data-testid="help-accordion">
          {HELP_SECTIONS.map((s) => (
            <AccordionSection key={s.key} item={s} />
          ))}
        </Accordion>
      </div>

      {/* Legal sections — collapsible */}
      <div>
        <h2 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.12em] mb-2.5">Legal &amp; policies</h2>
        <Accordion type="single" collapsible data-testid="legal-accordion">
          {LEGAL_SECTIONS.map((s) => (
            <AccordionSection key={s.key} item={s} />
          ))}
        </Accordion>
      </div>

      {/* Contact Support */}
      <Card className="border-0 shadow-sm bg-slate-50" data-testid="help-contact-card">
        <CardContent className="p-5">
          <h3 className="font-semibold text-slate-900 mb-3">Contact Support</h3>
          <div className="space-y-2">
            <div className="flex items-start gap-3 text-sm text-slate-700">
              <Mail className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
              <div>
                <a
                  href="mailto:support@shiftcover365.co.uk"
                  className="font-semibold text-slate-900 hover:text-[#DA291C] transition-colors"
                  data-testid="help-support-email"
                >
                  support@shiftcover365.co.uk
                </a>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                  {SUPPORT_SLA}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
